import React, { useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { Draggable } from "react-beautiful-dnd";
import classNames from "classnames";

import {
  makeStyles,
  Card,
  CardContent,
  CardMedia,
  Typography,
  IconButton,
  Tooltip
} from "@material-ui/core";
import { Cancel, CheckCircle, FileCopyOutlined } from "@material-ui/icons";
import blue from "@material-ui/core/colors/blue";
import green from "@material-ui/core/colors/green";

import { getAssetTypeInfo, resolveExistingCreativePreview } from "./fileTypeUtils";
import { isCreativeReady } from "../../../utils/creativeReadiness";
import { getDisplayDimensions, getDurationOptions } from "./dimensionUtils";

import {
  removeCreative,
  selectCreative,
  deselectCreative,
  editCreative,
  duplicateCreative
} from "../../../actions/app/creatives";

import defaultImagePath from "../../../utils/defaultImages/defaultCreative.jpg";
import HorizontalAssetBackupPreview from "./HorizontalAssetBackupPreview";
import { ROTATION_TYPES, WEIGHT_OPTIONS } from "../../../actions/app/constants";

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundSize: "contain"
  },
  card: {
    width: "100%",
    marginBottom: 16,
    position: "relative",
    display: "flex"
  },
  thumb: {
    width: 250,
    margin: 4
  },
  selected: {
    border: `2px solid ${blue[500]}`,
    backgroundColor: blue[50]
  },
  details: {
    textAlign: "left",
    width: "80%",
    // Clears the action cluster whether or not the ready check is showing,
    // so naming a creative does not reflow its own text.
    paddingRight: "96px"
  },
  // One row for status and actions. They used to be positioned absolutely
  // one at a time, which was fine while only ever one rendered — the check
  // replaced the remove button — and put them on top of each other once both
  // could show at once.
  actions: {
    position: "absolute",
    top: theme.spacing(0.5),
    right: theme.spacing(0.5),
    display: "flex",
    alignItems: "center"
  },
  readyIcon: {
    display: "flex",
    marginRight: theme.spacing(1)
  },
  detailsWithDuplicate: {
    paddingRight: "144px"
  },
  name: {
    wordBreak: "break-all"
  },
  fileName: {
    paddingBottom: theme.spacing(1),
    wordBreak: "break-all"
  },
  taxonomy: {
    wordBreak: "break-all",
    paddingBottom: theme.spacing(1),
    fontWeight: 500
  },
  thumbCard: {
    width: 250,
    margin: "1.5%",
    position: "relative"
  },
  pointer: {
    cursor: "pointer"
  },
  iconPlaceholder: {
    width: 250,
    margin: 4,
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  }
}));

const HorizontalAssetPreview = ({
  id,
  asset,
  index,
  isClickEnabled,
  isDragDisabled,
  isCardSelected,
  handleSelectedCard,
  isWeightEditable,
  isThumbnailEnabled,
  isWeightEnabled,
  isDuplicateEnabled,
  uploadLimit
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const location = useLocation();
  const assets = useSelector((state) => state.creatives);
  const rotationType = assets.rotationType;

  // Same duration list the naming step groups by, so the size printed on the
  // card always matches the header the card sits under.
  const typeGroups = useSelector((state) => state.typeGroups);
  const durationOptions = useMemo(() => getDurationOptions(typeGroups), [
    typeGroups
  ]);

  const handleClick = () => {
    if (isCardSelected) {
      dispatch(deselectCreative(asset.id));
    } else {
      dispatch(selectCreative(asset.id));
    }
    handleSelectedCard(asset.id);
  };

  const editCreativeWeight = (event) => (asset) => {
    if (event.key === "Enter" || event.type == "blur") {
      let editedCreative = { ...asset, weight: parseInt(event.target.value) };
      dispatch(editCreative(editedCreative));
    }
  };

  const renderRotationData = (index, asset, rotationType) => {
    switch (rotationType) {
      case ROTATION_TYPES.WEIGHTED:
        if (
          assets.weightDistribution === WEIGHT_OPTIONS.CUSTOM &&
          isWeightEditable
        ) {
          return (
            <>
              Weight:{" "}
              <input
                onKeyPress={(e) => editCreativeWeight(e)(asset)}
                onBlur={(e) => editCreativeWeight(e)(asset)}
                defaultValue={asset.weight}
                maxLength="3"
                size="1"
              />
            </>
          );
        } else if (location.pathname.includes("preloader")) {
          return '';
        } else {
          return `Weight: ${asset.weight}`;
        }
      case ROTATION_TYPES.SEQUENTIAL:
        return `Sequence: ${index + 1}`;
      default:
        return;
    }
  };

  const cardStyles = classNames({
    [classes.card]: true,
    [classes.pointer]: isClickEnabled,
    [classes.selected]: isCardSelected && isClickEnabled
  });

  // Opt-in per step. Only the backup step turns this on, so the copy control
  // can't appear on the naming/companion/options/review lists that share this
  // card. Existing GCM picks are excluded for the same reason as on the upload
  // step: a copy would mean a second creative on the same asset.
  const canDuplicate =
    isDuplicateEnabled &&
    asset.creativeRole === "PRIMARY" &&
    asset.dcmCreative === undefined;
  const atLimit = assets.allIds.length >= uploadLimit;

  const detailStyles = classNames({
    [classes.details]: true,
    [classes.detailsWithDuplicate]: canDuplicate
  });

  const isReady = isCreativeReady(asset);

  return (
    <Draggable
      draggableId={id}
      index={index}
      isDragDisabled={
        isDragDisabled || rotationType !== ROTATION_TYPES.SEQUENTIAL
      }
    >
      {(provided) => (
        <div {...provided.draggableProps} {...provided.dragHandleProps}>
          <Card
            innerRef={provided.innerRef}
            className={cardStyles}
            onClick={isClickEnabled ? handleClick : null}
          >
            {(() => {
              const { icon: FileIcon } = getAssetTypeInfo(asset);
              const existingPreview = resolveExistingCreativePreview(asset);
              if (isThumbnailEnabled && existingPreview && existingPreview.previewType === 'image') {
                return (
                  <CardMedia
                    className={classes.thumb}
                    image={existingPreview.url}
                    title={asset.file.name}
                    key={asset.id}
                    classes={{ root: classes.root }}
                  />
                );
              }
              if (FileIcon) {
                return (
                  <div className={classes.iconPlaceholder}>
                    <FileIcon style={{ fontSize: 80, color: "#9e9e9e" }} />
                  </div>
                );
              }
              if (isThumbnailEnabled && asset.preview) {
                return (
                  <CardMedia
                    className={classes.thumb}
                    image={asset.preview}
                    title={asset.file.name}
                    key={asset.id}
                    classes={{ root: classes.root }}
                  />
                );
              }
              return null;
            })()}
            <div className={detailStyles}>
              <CardContent className={classes.content}>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  component="p"
                  className={classes.taxonomy}
                >
                  Creative name:{" "}
                  {asset.taxonomy.name ? `${asset.taxonomy.name}` : "N/A"}
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  component="p"
                  className={classes.fileName}
                >
                  File name:{" "}
                  {asset.file.name
                    ? asset.file.name
                    : asset.name
                    ? asset.name
                    : "N/A"}
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  component="p"
                  className={classes.name}
                >
                  Dimensions: {getDisplayDimensions(asset, durationOptions)}
                  {asset.isRetina ? " (retina 2x)" : ""}
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  component="p"
                  className={classes.name}
                >
                  Type: {getAssetTypeInfo(asset).label}
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  component="p"
                  className={classes.name}
                >
                  {asset.creativeRole === "PRIMARY" && isWeightEnabled
                    ? renderRotationData(index, asset, rotationType)
                    : null}
                </Typography>
              </CardContent>
            </div>
            {/* Status and removal are separate concerns. The check used to
                replace the remove button, which meant finishing a creative
                also took away the only way to drop it — and SmartTraffic
                depends on removing from the review step. */}
            <div className={classes.actions}>
              {isReady ? (
                <Tooltip title="Ready to traffic">
                  <span className={classes.readyIcon}>
                    <CheckCircle style={{ color: green[500] }} />
                  </span>
                </Tooltip>
              ) : null}
              {canDuplicate ? (
                <Tooltip
                  title={
                    atLimit
                      ? `Upload limit is ${uploadLimit} files`
                      : asset.backupId
                      ? "Duplicate this creative and its backup"
                      : "Duplicate this creative"
                  }
                  placement="top"
                >
                  <span>
                    <IconButton
                      aria-label="Duplicate creative"
                      disabled={atLimit}
                      onClick={(e) => {
                        e.stopPropagation();
                        dispatch(duplicateCreative(asset.id));
                      }}
                    >
                      <FileCopyOutlined
                        color={atLimit ? "disabled" : "action"}
                      />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : null}
              <IconButton
                aria-label="Remove creative"
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(removeCreative(asset.id));
                }}
              >
                <Cancel filled="true" color="action" />
              </IconButton>
            </div>
          </Card>
          {asset.backupId && assets.byId[asset.backupId] ? (
            <HorizontalAssetBackupPreview
              id={asset.backupId}
              index={index}
              key={`${asset.backupId}-${index}`}
              asset={assets.byId[asset.backupId]}
              isClickEnabled={isClickEnabled}
              isDragDisabled={isDragDisabled}
              isCardSelected={isCardSelected}
              isThumbnailEnabled={isThumbnailEnabled}
              label="BACKUP"
            />
          ) : null}
          {asset.companionId && assets.byId[asset.companionId] ? (
            <HorizontalAssetBackupPreview
              id={asset.companionId}
              index={index}
              key={`${asset.companionId}-${index}`}
              asset={assets.byId[asset.companionId]}
              isClickEnabled={isClickEnabled}
              isDragDisabled={isDragDisabled}
              isCardSelected={false}
              isThumbnailEnabled={isThumbnailEnabled}
              label="COMPANION"
            />
          ) : null}
        </div>
      )}
    </Draggable>
  );
};

HorizontalAssetPreview.defaultProps = {
  isCardSelected: false,
  handleSelectedCard: () => {},
  isWeightEditable: false,
  isDuplicateEnabled: false,
  uploadLimit: 20
};

export default HorizontalAssetPreview;
